import * as THREE from "three";
import * as geolib from 'geolib';
import { mapSize } from "./TerrainControl";
import { logger } from "../../../../../services/loggingService";
import axios from "../../../../../helpers/bearerAxios";
import { CustomImageProperty, ImageryOptions } from "../../../../../@types/enums";
import { useViewerStore } from "../../../../../store/store";
import { Viewer } from "../Viewer-Factory/Viewer";
import { Coordinates } from "../../../../../@types/types";

export function ChangeMapMesh(self: Viewer, img: string, isCustom: boolean) {
    let image = new Image();
    image.src = img;
    image.onload = function () {
        setImagery(self, image);
        const {customImageScale, customImageRotation} = useViewerStore.getState();
        if (isCustom) useViewerStore.setState({customImagery: img});
        SetMapCustomSize(self, CustomImageProperty.scale, customImageScale);
        SetMapCustomSize(self, CustomImageProperty.rotation, customImageRotation);
    };
}

export function SetMapMesh(self: Viewer) {
    const {mapTexture, mapView, mapCustom} = self;
    if(!mapCustom) {
        logger.error('mapCustom is missing');
        return;
    } 

    if(mapView){
        mapCustom.material.map = mapTexture;
    }
    else {
        //set white color
        const whiteBackgroundMaterial = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            side: THREE.DoubleSide,
            transparent: false,
            opacity: 1
        });
        mapCustom.material = whiteBackgroundMaterial;
    }

    mapCustom.material.needsUpdate = true;
}

export function setImagery(self: Viewer, img: HTMLImageElement) {
    if(!self.mapCustom) {
        logger.error('mapCustom is missing');
        return;
    }
    let map = new THREE.TextureLoader().load(img.src);

    map.minFilter = THREE.LinearFilter;
    map.magFilter = THREE.LinearFilter;
    map.anisotropy = self.renderer.capabilities.getMaxAnisotropy();
    map.generateMipmaps = false;
    map.needsUpdate = true;

    //increase texture sharpness,brightness.
    map.wrapS = THREE.RepeatWrapping;
    map.wrapT = THREE.RepeatWrapping;
    map.repeat.set(1, 1);
    map.offset.set(0, 0);

    if (self.mapTexture) self.mapTexture.dispose();
    self.mapTexture = map;
    self.map64Binary = img.src.replace(/^data:image\/(png|jpg);base64,/, "");
    self.mapCustom.aspectRatio = img.naturalWidth / img.naturalHeight;
    SetMapMesh(self);
    SetMapCustomSize(self, CustomImageProperty.scale, 1);
}

export function SetMapCustomSize(self: Viewer, type: CustomImageProperty, value: number) {
    const {mapCustom} = self
    if (!mapCustom) return;
    const {scale} = mapCustom;
    const newScl = value / mapSize;
    const aspectRatio = mapCustom.aspectRatio ?? 1;
    if (type === 'scale'){
        scale.y = value;
        scale.x = value * aspectRatio;
    } else if (type === 'height' || type === 'width') {
        scale.y = newScl;
        scale.x = newScl * aspectRatio;
    } else if (type === 'rotation') mapCustom.rotation.z = Math.PI / 180 * value;
    else if (type === 'posX') {
        mapCustom.position.x = value;
    } else if (type === 'posY') {
        mapCustom.position.z = value;
    } else if (type === 'opacity') {
        //change opacity and transparency
        mapCustom.material.opacity = value;
        mapCustom.material.transparent = value < 1;
        mapCustom.material.needsUpdate = true;
    }
}

export function ViewAlertLatLon(self: Viewer, point: THREE.Vector3) {
    const offsetPoint = GetLatLon(self.mapCoordinates, point);
    console.log('LatLon Point:', point);
    console.log('LatLon Position:', offsetPoint);
    

    self.viewerAlert({
        show: true,
        isModal: false,
        title: 'Lat-Long Position',
        message: `Latitude: ${offsetPoint.latitude.toFixed(5)} \n Longitude: ${offsetPoint.longitude.toFixed(5)}`,
    })
}

export function FetchMapImage(self: Viewer, vendor: ImageryOptions) {
    const opportunityId = sessionStorage.getItem('SalesForceId');
    const { latitude, longitude } = self.mapCoordinates;

    axios.post(`${import.meta.env.VITE_BASE_URL}/api/Opportunities/${opportunityId}/StaticMaps/${vendor}`,
        { coordinates: { latitude, longitude }, size: mapSize },
        { responseType: 'blob', headers: { 'Content-Type': 'application/json' }}
    ).then(res => {
        const blob = new Blob([res.data], { type: res.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        ChangeMapMesh(self, url, false);
    }).catch((e) => logger.error(e, `${vendor} Map API Error`));
}

export async function fetchCustomMap(self: Viewer) {
    const oppId = sessionStorage.getItem('SalesForceId');
    let url = `${import.meta.env.VITE_BASE_URL}/api/Opportunities/${oppId}/StaticMaps/Custom`

    return axios.get(url, { responseType: 'blob' })
        .then(res => {
            if (res.status === 200){
                const blob = new Blob([res.data], { type: res.headers['content-type'] });
                const file = new File([blob], "custom-map.png", { type: blob.type });
                const url = window.URL.createObjectURL(blob);
                ChangeMapMesh(self, url, true);
                useViewerStore.setState({ customImageFile : file})
            } else {
                FetchMapImage(self, ImageryOptions.DEFAULT);
            }
        }).catch((e) => {
            logger.error(e, `Custom Map API Error`);
            FetchMapImage(self, ImageryOptions.DEFAULT);
        });
}

export async function uploadCustomMap(image: File) {
    const oppId = sessionStorage.getItem('SalesForceId');
    let url = `${import.meta.env.VITE_BASE_URL}/api/Opportunities/${oppId}/StaticMaps/Custom`

    const formData = new FormData();
    formData.append('file', image, 'custom.jpg');

    return await axios.post(url, formData)
        .then((response) => true)
        .catch((error) => {
            logger.error(error);
            return false;
        });
}

export function GetLatLon(mapCoordinates: Coordinates, point: {x:number, z:number}) : Coordinates {
    //point.z positive to negative and vice versa
    point.z = point.z < 0 ? Math.abs(point.z) : -Math.abs(point.z);

    // Define the offset distance in meters
    //calculate the distance from the center of the earth to the point on the plane
    const offsetDistance = Math.sqrt(Math.pow(point.x, 2) + Math.pow(point.z, 2));

    //Define bearing
    let bearing;

    //calculate the bearing
    if (point.x >= 0 && point.z >= 0) {
        bearing = Math.atan(point.x / point.z);
    } else if (point.x >= 0 && point.z < 0) {
        bearing = Math.atan(point.x / point.z) + Math.PI;
    } else if (point.x < 0 && point.z < 0) {
        bearing = Math.atan(point.x / point.z) + Math.PI;
    } else if (point.x < 0 && point.z >= 0) {
        bearing = Math.atan(point.x / point.z) + 2 * Math.PI;
    }

    //bearing value to high precision
    if (bearing !== undefined) {
        bearing = Math.round((bearing * 180 / Math.PI) * 1_000_000) / 1_000_000;
    } else {
        throw new Error("Bearing could not be calculated");
    }

    if (mapCoordinates.latitude === null || mapCoordinates.longitude === null) {
        throw new Error("Map coordinates are not set");
    }

    // Calculate the latitude and longitude of the offset point
    return geolib.computeDestinationPoint(
        { latitude: mapCoordinates.latitude, longitude: mapCoordinates.longitude },
        offsetDistance,
        bearing
    );
}


export function GetXYZ(mapCoordinates: Coordinates, latLon: Coordinates) {
    // Calculate the distance and bearing from the map reference point to the given lat/lon
    const distance = geolib.getDistance(mapCoordinates, latLon);
    const bearing = geolib.getGreatCircleBearing(mapCoordinates, latLon);

    // Convert bearing to radians
    const bearingRadians = bearing * Math.PI / 180;

    // Calculate the x and z coordinates (assuming y is up/down and ignoring elevation)
    const x = Math.sin(bearingRadians) * distance;
    const z = Math.cos(bearingRadians) * distance;

    // Assuming a flat Earth projection, y would be 0 or at sea level
    // If elevation is needed, it can be calculated separately
    const y = 0;

    // Invert z coordinate to match the original function's convention
    const zInverted = -z;

    // Return the 3D Cartesian coordinates
    return {x: x, y: y, z: zInverted};
}

export function DragMapCustom(self: Viewer, interPos: THREE.Vector3) {
    const {posPlaneDown, posMapDown } = self;
    if(!posPlaneDown || !posMapDown) {
        logger.error("posPlaneDown and/or posMapDown is undefined");
        return;
    }
    const deltaDis = {x: interPos.x - posPlaneDown.x, z: interPos.z - posPlaneDown.z};
    const newPosX = posMapDown.x + deltaDis.x,
        newPosZ = posMapDown.z + deltaDis.z;
    if ( Math.abs (newPosX) < mapSize / 2 ) {
        SetMapCustomSize(self, CustomImageProperty.posX, newPosX);
    }
    if ( Math.abs(newPosZ) < mapSize / 2 ) {
        SetMapCustomSize(self, CustomImageProperty.posY, newPosZ);
    }
}

export function centerMapPosition(self: Viewer) {
    SetMapCustomSize(self, CustomImageProperty.posX, 0);
    SetMapCustomSize(self, CustomImageProperty.posY, 0);
}

export function CenterMapCoodinates(self: Viewer, imageryType: ImageryOptions) {
    const {mapCustom} = self;
    if(!mapCustom) {
        logger.error("mapCustom is undefined");
        return;
    }
    const boundingBox = new THREE.Box3().setFromObject(self.roofGroup);
    const center = boundingBox.getCenter(new THREE.Vector3());
    const offsetPoint = new THREE.Vector3(center.x, 0, -center.z);
    const newMapCoordinates  =  GetLatLon(self.mapCoordinates, offsetPoint);
    useViewerStore.setState({ mapCoordinates : newMapCoordinates });
    self.mapCoordinates = newMapCoordinates;
    mapCustom.position.x = center.x;
    mapCustom.position.z = -center.z;
    FetchMapImage(self, imageryType || ImageryOptions.DEFAULT);
}